import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import cartBorder from "@assets/images/cartBorder.webp";
import shippingBorder from "@assets/images/borders/shippingBorder.webp";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import routes from "@routes/routes";
import LogoutModal from "@src/components/Logout/LogoutModal";
import { Helmet } from "react-helmet";

import contactBorder from "@assets/images/borders/contactBorder.webp";
const splitWord = (val: string) => {
  let words: string[] = [];
  words = val.split(" ");
  if (words.length === 4) {
    return [words[0] + " " + words[1], words[2] + " " + words[3]];
  }
  return words;
};

const Profile: React.FC = () => {
  const [profileRoutes] = useState(() => {
    const r = [
      { label: "My Account", value: "account", route: routes.account },
      {
        label: "Purchased Episodes & Music",
        value: "purchased",
        route: routes.purchased,
      },
      {
        label: "Rented Episodes",
        value: "rented",
        route: routes.rentedEpisodes,
      },
      { label: "Orders", value: "orders", route: routes.orderHistory },
      { label: "Downloads", value: "downloads", route: routes.downloads },
      { label: "Wishlist", value: "wishlist", route: routes.wishlist },
      { label: "Bookmarks", value: "bookmark", route: routes.bookmark },
      { label: "Addresses", value: "addresses", route: routes.addresses },
      {
        label: "Payment Methods",
        value: "paymentmethods",
        route: routes.paymentmethod,
      },
      {
        label: "Notifications Settings",
        value: "notifications-settings",
        route: routes.notificationSettings,
      },
    ];
    return r;
  });
  const [firstTab, setFirstTab] = useState<string>("My");
  const [lastTab, setLastTab] = useState<string>("Account");
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>("");

  const tabClass = useCallback(
    (tab: string) => {
      return `relative py-4 px-6 cursor-pointer duration-150 hover:opacity-100 max-md:hover:opacity-50 max-md:bg-primary max-md:p-3 max-sm:p-2 rounded-lg ${
        activeTab === tab ? "opacity-100 hover:!opacity-100" : "opacity-50"
      }`;
    },
    [activeTab]
  );

  const navigate = useNavigate();

  const handleTabClick = useCallback(
    (item: { label: string; value: string; route: string }) => {
      const [f, s] = splitWord(item.label);
      setFirstTab(f);
      setLastTab(s);
      navigate(item.route);
    },
    [navigate]
  );

  useEffect(() => {
    for (let i = 0; i < profileRoutes.length; i++) {
      if (location.pathname.includes(profileRoutes[i].route)) {
        setActiveTab(profileRoutes[i].value);
        const [f, s] = splitWord(profileRoutes[i].label);
        setFirstTab(f);
        setLastTab(s);
        break;
      }
    }
  }, [location.pathname, profileRoutes]);

  const sliderRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [, setStartX] = useState(0);
  const [, setScrollLeft] = useState(0);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;

    setIsLeftDisabled(scrollLeft === 0);
    setIsRightDisabled(scrollLeft >= scrollWidth - clientWidth - 1);
  }, []);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!sliderRef.current) return;

    setIsDragging(true);
    const pageX = "touches" in e ? e.touches[0].pageX : e.pageX;
    setStartX(pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;

    e.preventDefault();
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };
  useEffect(() => {
    if (window.innerWidth < 1024) {
      document.getElementById(activeTab)?.scrollIntoView({
        block: "nearest",
        behavior: "instant",
        inline: "center",
      });
    }
  }, [activeTab]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    // Update button state on load and scroll
    handleScroll();
    slider.addEventListener("scroll", handleScroll);

    return () => {
      slider.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      localStorage.removeItem("etype");
    };
  }, []);

  const [scrollPosition, setScrollPosition] = useState(0);
  useLayoutEffect(() => {
    if (showLogoutModal) {
      const currentScroll = window.scrollY;
      setScrollPosition(currentScroll);
      document.body.style.top = `-${currentScroll}px`;
      document.body.classList.add("!fixed", "overflow-hidden", "w-full");
    } else {
      document.body.classList.remove("!fixed", "overflow-hidden", "w-full");
      document.body.style.top = "";
      window.scrollTo(0, scrollPosition);
    }

    return () => {
      document.body.classList.remove("!fixed", "overflow-hidden", "w-full");
      document.body.style.top = "";
    };
  }, [showLogoutModal]);

  return (
    <main>
      <Helmet>
        <title>fatSu | Profile</title>
        <meta
          name="description"
          content="Manage your account details, review purchased or rented episodes and music, customize notification settings, and track your orders all from your profile."
        />
        <meta
          name="keywords"
          content="fatsu, fatsu profile, profile, account, purchased episode, purchased music, rented episode, rented episodes, orders, fatsu orders, downloads, Wishlist management, addresses, address, payment methods, notification settings, Purchase history, Order tracking"
        />
      </Helmet>
      <section className="section text-white py-10 max-sm:py-6 blue-bg-dots min-h-[calc(100vh-208px)]">
        <div className="container max-md:px-4">
          <h2 className="text-lightBlue text-6xl max-md:text-4xl max-sm:text-2xl text-start mb-10 max-sm:mb-4 break-words">
            {firstTab} <span className="text-primary">{lastTab}</span>
          </h2>

          <div className="grid grid-cols-3 gap-10 max-lg:gap-1 max-md:gap-4 items-start max-md:flex max-md:flex-col max-md:mt-3">
            <div className="relative py-10 px-8 max-xl:p-6 max-md:w-full max-md:p-0">
              <img
                className="absolute top-0 left-0 size-full max-md:hidden"
                src={cartBorder}
                alt="profile-border"
              />
              <div className="relative">
                <div className="flex flex-col max-md:flex-row gap-5 max-md:gap-2 max-md:m-0 max-md:overflow-auto mb-3 max-md:hidden">
                  {profileRoutes.map((route) => {
                    return (
                      <div
                        key={route.value}
                        className={tabClass(route.value)}
                        onClick={() => handleTabClick(route)}
                      >
                        <img
                          className="absolute top-0 left-0 size-full"
                          src={shippingBorder}
                          alt=""
                        />
                        <h6 className="text-primary max-md:text-black text-xl max-sm:text-base max-md:text-nowrap">
                          {route.label}
                        </h6>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-1 select-none md:hidden">
                  <button
                    className="text-4xl max-sm:text-3xl text-primary disabled:opacity-50"
                    onClick={() => {
                      if (sliderRef.current)
                        sliderRef.current.scrollLeft -= 100;
                    }}
                    disabled={isLeftDisabled}
                  >
                    <i className="ri-arrow-left-circle-line"></i>
                  </button>

                  <div
                    className="flex items-center gap-2 max-sm:gap-1 overflow-auto"
                    ref={sliderRef}
                    onMouseDown={handleDragStart}
                    onMouseLeave={handleDragEnd}
                    onMouseUp={handleDragEnd}
                    onMouseMove={handleDragMove}
                    onTouchStart={handleDragStart}
                    onTouchEnd={handleDragEnd}
                    onTouchMove={handleDragMove}
                  >
                    {profileRoutes.map((route) => {
                      return (
                        <div
                          key={route.value}
                          className={tabClass(route.value)}
                          onClick={() => handleTabClick(route)}
                          id={route.value}
                        >
                          <h6 className="text-primary max-md:text-black text-xl max-sm:text-base max-md:text-nowrap">
                            {route.label}
                          </h6>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    className="text-4xl max-sm:text-3xl text-primary disabled:opacity-50"
                    onClick={() => {
                      if (sliderRef.current)
                        sliderRef.current.scrollLeft += 100;
                    }}
                    disabled={isRightDisabled}
                  >
                    <i className="ri-arrow-right-circle-line"></i>
                  </button>
                </div>
                <div className="max-xl:text-center">
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      setShowLogoutModal(true);
                    }}
                    className="text-[#89D9EE] text-3xl ps-3 duration-150 hover:scale-95 max-sm:text-center max-sm:w-full max-md:hidden"
                    aria-label="Logout"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
            <div className="col-span-2 flex flex-col gap-10 max-md:col-auto max-md:w-full">
              <div className="relative">
                <img
                  className="absolute top-0 left-0 size-full max-md:hidden"
                  src={contactBorder}
                  alt=""
                />
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </section>
      {showLogoutModal && <LogoutModal setModal={setShowLogoutModal} />}
    </main>
  );
};

export default Profile;
